import { customElement, property, query } from 'lit/decorators.js';
import { html, TemplateResult } from 'lit';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { ViewConversationAttachment } from '../../api/dealer-api-interface-franchisee';

import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangFranchiseeEngineeredToOrder } from '../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-engineered-to-order-lang';
import { WebModuleConversationEntryDropZone } from './wm-conversation-entry-dropzone';

/**
 * <wm-eto-masterdocument-uploader>
 * Specialized wrapper for master document (shop drawing) upload in ETO workflow.
 * Differences from <wmview-conversation-entry-editor>:
 *  - Accepts conversationEntryId instead of a full conversationEntry object.
 *  - Internalizes fixed configuration: allowDownloadLinks=true, maxFiles=1, forceUpdate=true,
 *    attachmentsOnly=true, autoSaveWhenAttachmentsOnly=false, showHeader=false.
 *  - Emits a simplified attachment upload event: 'wm-eto-masterdocument-uploaded'
 *    with { attachments, conversationAttachments } payload.
 *  - Caller supplies conversationId & conversationEntryId plus readonly flag and optional caption override.
 */
@customElement('wm-eto-masterdocument-uploader')
export class WMEtoMasterDocumentUploader extends LitElementBase {
  /** Conversation Id to associate with uploads */
  @property({ type: String }) masterDocumentConversationId: string = '';
  /** When true, disables interaction */
  @property({ type: Boolean }) disabled: boolean = false;
  /** Optional override for caption (defaults to shop drawing localization). */
  @property({ type: String }) caption: string | null = null;
  /** Existing attachments (non-embedded) for display */
  @property({ type: String }) masterDocumentFilename: string = '';
  /** Public URL for the existing master document attachment */
  @property({ type: String }) masterDocumentPublicUrl: string = '';
  @query('#attachments')
  attachmentsEditor?: WebModuleConversationEntryDropZone;

  // Fixed private configuration
  private readonly _allowDownloadLinks = true;
  private readonly _maxFiles = 1;

  private get _caption() {
    return this.caption ?? LangFranchiseeEngineeredToOrder.shopDrawing();
  }

  private eventAttachmentsChanged = (e: CustomEvent<{ isUploading: boolean; files: ViewConversationAttachment[] }>) => {
    // Forward uploaded attachments list via custom event
    this.dispatchEvent(
      new CustomEvent('wm-eto-masterdocument-uploaded', {
        bubbles: true,
        composed: true,
        detail: {
          masterDocumentFilename: e.detail.files[0]?.filename, // minimal AddConversationAttachment shape

          conversationId: this.masterDocumentConversationId
        }
      })
    );
  };

  private attachmentsTemplate(): TemplateResult {
    const attachments = isEmptyOrSpace(this.masterDocumentFilename)
      ? []
      : [
          {
            virtualPath: '',
            publicUrl: this.masterDocumentPublicUrl || '',
            size: 0,
            embedded: false,
            filename: this.masterDocumentFilename
          }
        ];
    return html`<wmview-conversation-attachments
      id="attachments"
      .maxFiles=${this._maxFiles}
      .readonly=${this.disabled}
      .allowDownloadLinks=${this._allowDownloadLinks}
      .caption=${this._caption}
      @wm-event-changed=${this.eventAttachmentsChanged}
      .conversationId=${this.masterDocumentConversationId}
      .conversationEntryId=${this.masterDocumentConversationId}
      .existingFiles=${attachments}
    ></wmview-conversation-attachments>`;
  }

  // Compatibility getters (mirror subset of wmview-conversation-entry-editor API used elsewhere)
  public get allAttachments(): ViewConversationAttachment[] {
    return [...(this.attachmentsEditor?.existingFiles ?? [])];
  }
  public get allAttachmentsAsInputs(): { filename: string }[] {
    return [...(this.attachmentsEditor?.allFilesAsInputs ?? [])];
  }

  render(): TemplateResult {
    return html`<div class="eto-master-document-uploader">${this.attachmentsTemplate()}</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wm-eto-masterdocument-uploader': WMEtoMasterDocumentUploader;
  }
}
